import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { useAuth } from '../context/AuthContext';
import { MASCOT_URL } from '../assets/mascot';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Plus, Trash2, FileDown, Edit, Receipt, CheckCircle2, Settings,
  Euro, Send, Clock, XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  brouillon: { label: 'Brouillon', cls: 'bg-slate-100 text-slate-700 border-slate-300', icon: Edit },
  envoyee: { label: 'Envoyée', cls: 'bg-blue-100 text-blue-700 border-blue-300', icon: Send },
  payee: { label: 'Payée', cls: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2 },
  annulee: { label: 'Annulée', cls: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
};

const money = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const emptyInvoice = () => ({
  client: { nom: '', adresse: '', code_postal: '', ville: '', email: '', telephone: '' },
  lines: [{ designation: '', quantite: 1, prix_unitaire_ht: 0 }],
  tva_rate: 20,
  remise: 0,
  acompte: 0,
  date_emission: new Date().toISOString().slice(0, 10),
  date_echeance: '',
  notes: '',
  status: 'brouillon',
  intervention_id: null,
});

const computeTotals = (inv) => {
  const ht = (inv.lines || []).reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0), 0) - (Number(inv.remise) || 0);
  const tva = ht * (Number(inv.tva_rate) || 0) / 100;
  const ttc = ht + tva;
  const net = ttc - (Number(inv.acompte) || 0);
  return { ht, tva, ttc, net };
};

const Invoices = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isAdmin = user?.role === 'admin';

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyInvoice());
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [company, setCompany] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/invoices`);
      setInvoices(r.data);
    } catch (e) {
      toast.error('Erreur de chargement des factures');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Prefill from intervention query param
  useEffect(() => {
    const iid = params.get('intervention');
    if (iid) {
      axios.get(`${API}/invoices/prefill/intervention/${iid}`)
        .then((r) => {
          setForm({ ...emptyInvoice(), ...r.data, client: { ...emptyInvoice().client, ...r.data.client } });
          setEditingId(null);
          setDialogOpen(true);
        })
        .catch(() => toast.error('Impossible de préremplir depuis l\'intervention'));
    }
  }, [params]);

  const openNew = () => { setForm(emptyInvoice()); setEditingId(null); setDialogOpen(true); };

  const openEdit = (inv) => {
    setForm({
      client: { ...emptyInvoice().client, ...(inv.client || {}) },
      lines: inv.lines?.length ? inv.lines : [{ designation: '', quantite: 1, prix_unitaire_ht: 0 }],
      tva_rate: inv.tva_rate ?? 20,
      remise: inv.remise ?? 0,
      acompte: inv.acompte ?? 0,
      date_emission: (inv.date_emission || '').slice(0, 10),
      date_echeance: (inv.date_echeance || '').slice(0, 10),
      notes: inv.notes || '',
      status: inv.status || 'brouillon',
      intervention_id: inv.intervention_id || null,
    });
    setEditingId(inv.id);
    setDialogOpen(true);
  };

  const setClient = (k, v) => setForm((f) => ({ ...f, client: { ...f.client, [k]: v } }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { designation: '', quantite: 1, prix_unitaire_ht: 0 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.client.nom.trim()) { toast.error('Le nom du client est requis'); return; }
    if (!form.lines.some((l) => l.designation.trim())) { toast.error('Ajoutez au moins une ligne'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lines: form.lines.filter((l) => l.designation.trim()).map((l) => ({
          designation: l.designation, quantite: Number(l.quantite) || 0, prix_unitaire_ht: Number(l.prix_unitaire_ht) || 0,
        })),
        tva_rate: Number(form.tva_rate) || 0,
        remise: Number(form.remise) || 0,
        acompte: Number(form.acompte) || 0,
        date_emission: form.date_emission ? new Date(form.date_emission).toISOString() : null,
        date_echeance: form.date_echeance ? new Date(form.date_echeance).toISOString() : null,
      };
      if (editingId) {
        await axios.put(`${API}/invoices/${editingId}`, payload);
        toast.success('Facture mise à jour');
      } else {
        await axios.post(`${API}/invoices`, payload);
        toast.success('Facture créée');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (inv, status) => {
    try {
      await axios.patch(`${API}/invoices/${inv.id}/status`, { status });
      toast.success(`Statut : ${STATUS[status].label}`);
      load();
    } catch (e) { toast.error('Erreur mise à jour statut'); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Supprimer la facture ${inv.invoice_number} ?`)) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`);
      toast.success('Facture supprimée');
      load();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const downloadPdf = async (inv) => {
    try {
      toast.info('Génération du PDF...');
      const r = await axios.get(`${API}/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      saveAs(r.data, `${inv.invoice_number}.pdf`);
    } catch (e) { toast.error('Erreur PDF'); }
  };

  const openSettings = async () => {
    try {
      const r = await axios.get(`${API}/settings/company`);
      setCompany(r.data);
      setSettingsOpen(true);
    } catch (e) { toast.error('Erreur chargement réglages'); }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/settings/company`, company);
      toast.success('Réglages société enregistrés');
      setSettingsOpen(false);
    } catch (e) { toast.error('Erreur enregistrement réglages'); }
  };

  const totals = computeTotals(form);
  const stats = {
    total: invoices.reduce((s, i) => s + computeTotals(i).ttc, 0),
    paid: invoices.filter((i) => i.status === 'payee').reduce((s, i) => s + computeTotals(i).ttc, 0),
    pending: invoices.filter((i) => i.status !== 'payee' && i.status !== 'annulee').reduce((s, i) => s + computeTotals(i).ttc, 0),
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="invoices-page">
      <header className="bg-gradient-to-r from-[#1A1A1A] via-[#2D2D2D] to-[#1A1A1A] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" className="text-white hover:bg-[#8B7355]/20" onClick={() => nav(isAdmin ? '/admin' : '/secretary')} data-testid="invoices-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />Retour
          </Button>
          <h1 className="text-white font-bold text-lg flex items-center gap-2"><Receipt className="h-5 w-5 text-[#8B7355]" />Facturation</h1>
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={openSettings} className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white" data-testid="company-settings-btn">
                <Settings className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Société</span>
              </Button>
            )}
            <Button onClick={openNew} className="bg-[#8B7355] hover:bg-[#7A6548] text-white font-bold" data-testid="new-invoice-btn">
              <Plus className="h-4 w-4 mr-2" />Nouvelle facture
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 border-[#8B7355]/20">
            <p className="text-sm text-[#6B7280]">Total facturé (TTC)</p>
            <p className="text-2xl font-bold text-[#1A1A1A]" data-testid="stat-total">{money(stats.total)}</p>
          </Card>
          <Card className="p-4 border-green-200 bg-green-50/40">
            <p className="text-sm text-[#6B7280]">Encaissé</p>
            <p className="text-2xl font-bold text-green-700" data-testid="stat-paid">{money(stats.paid)}</p>
          </Card>
          <Card className="p-4 border-amber-200 bg-amber-50/40">
            <p className="text-sm text-[#6B7280]">En attente</p>
            <p className="text-2xl font-bold text-amber-700" data-testid="stat-pending">{money(stats.pending)}</p>
          </Card>
        </div>

        {loading ? (
          <p className="text-center text-[#6B7280] py-12">Chargement...</p>
        ) : invoices.length === 0 ? (
          <Card className="p-12 text-center border-dashed border-2 border-[#8B7355]/20" data-testid="invoices-empty">
            <img src={MASCOT_URL} alt="Mascotte KARDIF" className="h-28 w-auto mx-auto mb-3 drop-shadow" />
            <p className="text-[#6B7280] mb-4">Aucune facture pour le moment</p>
            <Button onClick={openNew} className="bg-[#8B7355] hover:bg-[#7A6548] text-white"><Plus className="h-4 w-4 mr-2" />Créer la première facture</Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {invoices.map((inv) => {
              const t = computeTotals(inv);
              const st = STATUS[inv.status] || STATUS.brouillon;
              const StIcon = st.icon;
              return (
                <Card key={inv.id} className="p-4 border-[#8B7355]/15 hover:shadow-md transition-shadow" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-[#1A1A1A]">{inv.invoice_number}</span>
                        <Badge className={`${st.cls} border`}><StIcon className="h-3 w-3 mr-1" />{st.label}</Badge>
                      </div>
                      <p className="text-sm text-[#374151] mt-1 truncate">{inv.client?.nom} · {new Date(inv.date_emission).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#8B7355]">{money(t.ttc)}</p>
                      <p className="text-xs text-[#6B7280]">TTC</p>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(inv)} className="border-[#8B7355]/40 text-[#8B7355]" data-testid={`invoice-pdf-${inv.invoice_number}`}><FileDown className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(inv)} data-testid={`invoice-edit-${inv.invoice_number}`}><Edit className="h-4 w-4" /></Button>
                      {inv.status !== 'payee' && (
                        <Button size="sm" variant="outline" onClick={() => changeStatus(inv, 'payee')} className="border-green-300 text-green-700 hover:bg-green-50" data-testid={`invoice-mark-paid-${inv.invoice_number}`}><CheckCircle2 className="h-4 w-4" /></Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => remove(inv)} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`invoice-delete-${inv.invoice_number}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== Create / Edit dialog ===== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifier la facture' : 'Nouvelle facture'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Client */}
            <div>
              <Label className="text-[#8B7355] font-semibold">Client</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <Input placeholder="Nom / Société *" value={form.client.nom} onChange={(e) => setClient('nom', e.target.value)} data-testid="invoice-client-nom" />
                <Input placeholder="Téléphone" value={form.client.telephone} onChange={(e) => setClient('telephone', e.target.value)} data-testid="invoice-client-tel" />
                <Input placeholder="Adresse" value={form.client.adresse} onChange={(e) => setClient('adresse', e.target.value)} className="sm:col-span-2" data-testid="invoice-client-adresse" />
                <Input placeholder="Code postal" value={form.client.code_postal} onChange={(e) => setClient('code_postal', e.target.value)} />
                <Input placeholder="Ville" value={form.client.ville} onChange={(e) => setClient('ville', e.target.value)} />
                <Input placeholder="Email" value={form.client.email} onChange={(e) => setClient('email', e.target.value)} className="sm:col-span-2" />
              </div>
            </div>

            {/* Lines */}
            <div>
              <Label className="text-[#8B7355] font-semibold">Prestations</Label>
              <div className="space-y-2 mt-2">
                {form.lines.map((l, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`invoice-line-${i}`}>
                    <Input placeholder="Désignation" value={l.designation} onChange={(e) => setLine(i, 'designation', e.target.value)} className="flex-1" data-testid={`line-designation-${i}`} />
                    <Input type="number" step="0.5" placeholder="Qté" value={l.quantite} onChange={(e) => setLine(i, 'quantite', e.target.value)} className="w-16" data-testid={`line-qte-${i}`} />
                    <Input type="number" step="0.01" placeholder="P.U. HT" value={l.prix_unitaire_ht} onChange={(e) => setLine(i, 'prix_unitaire_ht', e.target.value)} className="w-24" data-testid={`line-pu-${i}`} />
                    <span className="w-24 text-right text-sm font-medium text-[#374151] hidden sm:block">{money((Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0))}</span>
                    <Button size="icon" variant="ghost" onClick={() => removeLine(i)} className="text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addLine} className="border-[#8B7355]/40 text-[#8B7355]" data-testid="add-line-btn"><Plus className="h-4 w-4 mr-1" />Ajouter une ligne</Button>
              </div>
            </div>

            {/* Params */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">TVA (%)</Label>
                <Input type="number" step="0.1" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} data-testid="invoice-tva" />
              </div>
              <div>
                <Label className="text-xs">Remise HT (€)</Label>
                <Input type="number" step="0.01" value={form.remise} onChange={(e) => setForm({ ...form, remise: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Acompte (€)</Label>
                <Input type="number" step="0.01" value={form.acompte} onChange={(e) => setForm({ ...form, acompte: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date émission</Label>
                <Input type="date" value={form.date_emission} onChange={(e) => setForm({ ...form, date_emission: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Échéance</Label>
                <Input type="date" value={form.date_echeance} onChange={(e) => setForm({ ...form, date_echeance: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ex: Merci pour votre confiance" />
              </div>
            </div>

            {/* Totals */}
            <div className="bg-[#F5F0EB] rounded-xl p-4 space-y-1.5">
              <div className="flex justify-between text-sm"><span>Total HT</span><span className="font-medium" data-testid="total-ht">{money(totals.ht)}</span></div>
              <div className="flex justify-between text-sm"><span>TVA ({Number(form.tva_rate) || 0} %)</span><span className="font-medium">{money(totals.tva)}</span></div>
              <div className="flex justify-between text-base font-bold text-[#8B7355] pt-1 border-t border-[#8B7355]/20"><span>Total TTC</span><span data-testid="total-ttc">{money(totals.ttc)}</span></div>
              {(Number(form.acompte) || 0) > 0 && (
                <div className="flex justify-between text-sm font-semibold"><span>Net à payer</span><span>{money(totals.net)}</span></div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#8B7355] hover:bg-[#7A6548] text-white" data-testid="save-invoice-btn">
              {saving ? 'Enregistrement...' : (editingId ? 'Mettre à jour' : 'Créer la facture')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Company settings dialog ===== */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-settings-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-[#8B7355]" />Coordonnées de la société</DialogTitle></DialogHeader>
          {company && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Raison sociale" value={company.raison_sociale || ''} onChange={(e) => setCompany({ ...company, raison_sociale: e.target.value })} className="sm:col-span-2" data-testid="company-raison-sociale" />
              <Input placeholder="Adresse" value={company.adresse || ''} onChange={(e) => setCompany({ ...company, adresse: e.target.value })} className="sm:col-span-2" />
              <Input placeholder="Code postal" value={company.code_postal || ''} onChange={(e) => setCompany({ ...company, code_postal: e.target.value })} />
              <Input placeholder="Ville" value={company.ville || ''} onChange={(e) => setCompany({ ...company, ville: e.target.value })} />
              <Input placeholder="Téléphone" value={company.telephone || ''} onChange={(e) => setCompany({ ...company, telephone: e.target.value })} />
              <Input placeholder="Email" value={company.email || ''} onChange={(e) => setCompany({ ...company, email: e.target.value })} />
              <Input placeholder="SIRET" value={company.siret || ''} onChange={(e) => setCompany({ ...company, siret: e.target.value })} />
              <Input placeholder="N° TVA intracommunautaire" value={company.tva_intra || ''} onChange={(e) => setCompany({ ...company, tva_intra: e.target.value })} />
              <Input placeholder="IBAN" value={company.iban || ''} onChange={(e) => setCompany({ ...company, iban: e.target.value })} />
              <Input placeholder="BIC" value={company.bic || ''} onChange={(e) => setCompany({ ...company, bic: e.target.value })} />
              <Textarea placeholder="Mentions légales (pénalités de retard...)" value={company.mentions || ''} onChange={(e) => setCompany({ ...company, mentions: e.target.value })} className="sm:col-span-2" rows={3} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Annuler</Button>
            <Button onClick={saveSettings} className="bg-[#8B7355] hover:bg-[#7A6548] text-white" data-testid="save-company-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
